/* eslint-disable @next/next/no-img-element */
"use client";

import { Icons } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { SocialIcon } from "@/components/ui/social-icon";
import { User } from "@/lib/auth";
import clsx from "clsx";
import {
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YoutubeIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AuthLink } from "@/components/auth-link";
import { NavLink } from "../(dashboard)/dashboard/nav-link";
import { LogoutMenuButton } from "./components";
import { SearchDialog } from "./search-bar";

export function MobileNavMenu({ user }: { user?: User }) {
  const pathname = usePathname();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
  }, [isOpen]);

  // State for hiding the navbar on scroll
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Hide navbar on scroll down, show on scroll up
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);


  return (
    <div className="pb-20">
      <div className={clsx(
        "fixed inset-x-0 bottom-0 z-50 lg:hidden transition-transform duration-300",
        {
          "translate-y-full": !isVisible,
          "translate-y-0": isVisible,
        }
      )}>
        <div className="grid content-center h-20 grid-cols-4 border-t shadow-2xl border-white/10 bg-theme-black2 dark:bg-white dark:shadow-2xl dark:border-t dark:border-[#bfbfbf]/30 rounded-t-[10px]">
          <Link
            href="/"
            className={clsx(
              "flex flex-col items-center space-y-2 text-sm text-center text-gray-400",
              {
                "text-theme-accent": pathname === "/" && !isOpen,
              }
            )}
          >
            <Icons.home className="w-6 h-6" />
            <span>Home</span>
          </Link>
          <Link
            href={user ? `${process.env.NEXT_PUBLIC_DASHBOARD_BASEURL}/authenticate` : "/sign-in"}
            className={clsx(
              "flex flex-col items-center space-y-2 text-sm text-center text-gray-400",
              {
                "text-theme-accent": pathname.startsWith("#"),
              }
            )}
          >
            <Icons.ticket className="w-6 h-6" />
            <span>Create</span>
          </Link>
          <SearchDialog>
            <button
              type="button"
              className="flex flex-col items-center space-y-2 text-sm text-center text-gray-400"
            >
              <Icons.search className="w-6 h-6" />
              <span>Search</span>
            </button>
          </SearchDialog>
          <Link
            href={user ? "/dashboard/account" : "/sign-in"}
            className={clsx(
              "hidden lg:flex flex-col items-center space-y-2 text-sm text-center text-gray-400",
              {
                "text-theme-accent": pathname.startsWith("/dashboard/account"),
              }
            )}
          >
            <Icons.account className="w-6 h-6" />
            <span>Account</span>
          </Link>
          <div className="flex lg:hidden justify-center">
            <MobileSheetMenu
              user={user}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileSheetMenu({
  user,
  isOpen,
  setIsOpen,
}: {
  user?: User;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <>
      <button
        type="button"
        className={clsx(
          "flex flex-col items-center space-y-2 text-sm text-center text-gray-400",
          {
            "text-theme-accent": isOpen,
          }
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icons.account className="w-6 h-6" />
        <span>Account</span>
      </button>
      {ready
        ? createPortal(
          <div
            className={clsx(
              "fixed inset-0 z-50 bg-theme-black3 dark:bg-theme-dark_card2 transition-transform overflow-y-auto text-white pb-20 px-4 flex flex-col",
              {
                "translate-x-0": isOpen,
                "translate-x-full": !isOpen,
              }
            )}
            onClick={(e) => {
              // Close sidebar on mobile when clicking on a link

              (e.target as Element).tagName === "A" && setIsOpen(false);
            }}
          >
            <Link href="/" className="block dark:hidden py-8">
              <Image
                src="/logo.png"
                className="object-contain mx-auto"
                width={98}
                height={44}
                alt="Mastermind logo"
              />
            </Link>
            <Link href="/" className="hidden dark:block py-8">
              <Image
                src="/logo-dark.svg"
                className="object-contain mx-auto"
                width={98}
                height={44}
                alt="Mastermind logo"
              />
            </Link>
            <div className="py-6 space-y-4">
              {user ? (
                <>
                  <div className="flex items-center px-4 py-4 space-x-2 bg-gray-800 dark:bg-theme-gray/10 rounded-md shrink-0">
                    <img
                      src={user.profile_photo_url}
                      alt="Profile photo"
                      className="w-6 h-6 border-white rounded-full border-px"
                    />

                    <div>
                      <div className="text-sm dark:text-theme-dark">{user.name}</div>
                      <div className="text-xs dark:text-theme-gray">{user.email}</div>
                    </div>
                  </div>
                  <NavLink href="/dashboard">
                    <Icons.allEvents className="w-4 h-4 shrink-0" />
                    <span className="ml-2.5 font-medium text-white dark:text-theme-dark text-sm xl:text-base">
                      Dashboard
                    </span>
                  </NavLink>
                  <NavLink href="/dashboard/ticket">
                    <Icons.ticket className="w-4 h-4 shrink-0" />
                    <span className="ml-2.5 font-medium text-white dark:text-theme-dark text-sm xl:text-base">
                      Tiket Saya
                    </span>
                  </NavLink>
                  <NavLink href="/dashboard/affiliate">
                    <Icons.megaphone className="w-4 h-4 shrink-0" />
                    <span className="ml-2.5 font-medium text-white dark:text-theme-dark text-sm">
                      Affiliate
                    </span>
                  </NavLink>
                  <NavLink href="/dashboard/account">
                    <Icons.account className="w-4 h-4 shrink-0" />
                    <span className="ml-2.5 font-medium text-white dark:text-theme-dark text-sm">
                      Informasi Akun
                    </span>
                  </NavLink>
                  <LogoutMenuButton />
                </>
              ) : (
                <div className="grid gap-3">
                  <Button asChild className="h-11 w-full text-base">
                    <AuthLink to="/sign-in">Masuk</AuthLink>
                  </Button>
                  <Button asChild variant="outline" className="h-11 w-full text-base text-theme-accent border-theme-accent">
                    <AuthLink to="/sign-up">Daftar</AuthLink>
                  </Button>
                </div>
              )}
            </div>

            <div className="flex-1"></div>

            <div className="flex items-center justify-center space-x-3 px-10 py-6 text-[11px] font-semibold flex-wrap text-center">
              <Link href="/payment-confirm" className="text-theme-gray text-center">Konfirmasi Pemnbayaran</Link>
              <div className="dark:text-theme-gray">·</div>
              <Link href="/page/about" className="text-theme-gray text-center">Tentang Mastermind</Link>
              <div className="dark:text-theme-gray">·</div>
              <Link href="/page/privacy-policy" className="text-theme-gray text-center">Kebijakan Privasi</Link>
              <div className="dark:text-theme-gray">·</div>
              <Link href="/page/terms-and-condition" className="text-theme-gray text-center">Syarat dan Ketentuan</Link>
              <div className="dark:text-theme-gray">·</div>
              <Link href="/page/faq" className="text-theme-gray text-center">FAQ</Link>
            </div>

            <div className="flex items-center justify-center pb-4 space-x-8">
              <SocialIcon
                icon={<FacebookIcon />}
                href="https://www.facebook.com/mastermindeo"
              />
              <SocialIcon
                icon={<TwitterIcon />}
                href="https://www.facebook.com/mastermindeo"
              />
              <SocialIcon
                icon={<YoutubeIcon />}
                href="https://www.facebook.com/mastermindeo"
              />
              <SocialIcon
                icon={<InstagramIcon />}
                href="https://www.facebook.com/mastermindeo"
              />
            </div>

            <div className="flex-1"></div>
          </div>,

          // @ts-ignore
          typeof window !== "undefined" ? document.body : null
        )
        : null}
    </>
  );
}
